import {
	type AdminPermission,
	type Role,
	type User,
	useAuthStore,
} from 'shared/store/auth'
import type { Permissions } from './permissions-type'

type PermissionCheck<Data = never> =
	| boolean
	| ([Data] extends [never]
			? (user: User) => boolean
			: (user: User, data: Data) => boolean)

type RolesWithPermissions = {
	[R in Role]: Partial<{
		[Key in keyof Permissions]: Partial<
			{
				[Action in Permissions[Key]['action'] & string]: PermissionCheck<any>
			} & {
				accessByDefault?: PermissionCheck<any>
			}
		>
	}>
}

const ROLES = {
	admin: {
		auth: { accessByDefault: true },
		health: { accessByDefault: true },
		dashboard: { Summary: (user) => hasAdminPermission(user) },
		users: {
			// The client directory exists to service refunds, which are finance-only.
			accessByDefault: (user) => hasAdminPermission(user, 'finance_admin'),
			// Lots admins and sellers still look up an initiator by TezId when
			// creating a lot.
			FindByTezId: (user) => hasAdminPermission(user),
		},
		settings: {
			Get: (user) => hasAdminPermission(user, 'super_admin'),
			Update: (user) => hasAdminPermission(user, 'super_admin'),
		},
		purchases: {
			accessByDefault: (user) => hasAdminPermission(user, 'finance_admin'),
		},
		topups: {
			accessByDefault: (user) => hasAdminPermission(user, 'finance_admin'),
		},
		wallets: {
			accessByDefault: (user) => hasAdminPermission(user, 'finance_admin'),
		},
		lots: {
			accessByDefault: (user) => hasAdminPermission(user, 'lots_admin', 'seller'),
			// A seller has no other creators to filter by.
			Creators: (user) => hasAdminPermission(user, 'lots_admin'),
		},
		categories: {
			// Read the taxonomy to tag a lot; only lots_admin may change it.
			accessByDefault: (user) => hasAdminPermission(user, 'lots_admin'),
			List: (user) => hasAdminPermission(user, 'lots_admin', 'seller'),
		},
	},
	user: {
		auth: { accessByDefault: true },
		health: { accessByDefault: true },
	},
} as const satisfies RolesWithPermissions

export function hasAdminPermission(
	user: Pick<User, 'role' | 'adminPermissions'>,
	...required: AdminPermission[]
): boolean {
	if (user.role !== 'admin' || user.adminPermissions.length === 0) return false
	if (user.adminPermissions.includes('super_admin')) return true
	return required.length === 0
		? true
		: required.some((permission) => user.adminPermissions.includes(permission))
}

type ExtractDataParam<T> = T extends (user: User, data: infer D) => boolean
	// A one-argument check still matches the two-argument shape, inferring
	// `unknown`; treat that as "takes no data" so callers don't have to pass one.
	? unknown extends D
		? never
		: D
	: never

type ExtractActionsFromRoles<Resource extends keyof Permissions> = {
	[R in Role]: (typeof ROLES)[R] extends {
		[K in Resource]?: infer ResourcePerms
	}
		? ResourcePerms extends Record<string, any>
			? 'accessByDefault' extends keyof ResourcePerms
				? Permissions[Resource]['action']
				: Exclude<keyof ResourcePerms, 'accessByDefault'>
			: never
		: never
}[Role]

type CollectDataTypes<
	Resource extends keyof Permissions,
	Action extends string,
> = {
	[R in Role]: (typeof ROLES)[R] extends {
		[K in Resource]?: infer ResourcePerms
	}
		? ResourcePerms extends { [A in Action]?: infer ActionPerm }
			? ExtractDataParam<ActionPerm>
			: never
		: never
}[Role]

type InferPermissionData<
	Resource extends keyof Permissions,
	Action extends string,
> =
	CollectDataTypes<Resource, Action> extends never
		? void
		: CollectDataTypes<Resource, Action>

export function checkPermission<
	Resource extends keyof Permissions,
	Action extends ExtractActionsFromRoles<Resource> & string,
>(
	resource: Resource,
	action: Action,
	...args: InferPermissionData<Resource, Action> extends void
		? []
		: [data: InferPermissionData<Resource, Action>]
): boolean

export function checkPermission<
	Resource extends keyof Permissions,
	Action extends string,
>(resource: Resource, action: Action, data?: any): boolean {
	const user = useAuthStore.getState().user
	if (!user) return false

	const rolePermissions = (ROLES as RolesWithPermissions)[user.role][resource]
	const permission =
		rolePermissions?.[action] ||
		(rolePermissions?.[action] !== false && rolePermissions?.accessByDefault)

	if (permission == null) return false
	if (typeof permission === 'boolean') return permission

	return permission(user, data)
}
